import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShoppingBag, Receipt, CreditCard, Package, Smartphone, BarChart3,
  Store, Network, Barcode, UtensilsCrossed, Monitor, Globe, ArrowRight,
} from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";
import { TopRelatedDigitalItems } from "@/components/sections/top-related-digital-items";

export const Route = createFileRoute("/software/pos/")({
  component: PosIndex,
  head: () => ({
    meta: [
      { title: "POS Software — CoderLity" },
      { name: "description", content: "Modern point of sale for retail and restaurants — fast checkout, inventory sync and offline support." },
      { property: "og:title", content: "POS Software — CoderLity" },
      { property: "og:description", content: "Retail and restaurant POS with offline-first checkout, inventory and multi-location reporting." },
      { property: "og:url", content: "/software/pos" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/software/pos" }],
  }),
});

type Item = { slug: string; title: string; desc: string; icon: typeof Store };
const retail: Item[] = [
  { slug: "single-store-pos", title: "Single-store POS", desc: "Explore single-store pos", icon: Store },
  { slug: "multi-branch-pos", title: "Multi-branch POS", desc: "Explore multi-branch pos", icon: Network },
  { slug: "barcode-inventory", title: "Barcode & Inventory", desc: "Explore barcode & inventory", icon: Barcode },
];
const restaurant: Item[] = [
  { slug: "table-management", title: "Table Management", desc: "Explore table management", icon: UtensilsCrossed },
  { slug: "kitchen-display", title: "Kitchen Display", desc: "Explore kitchen display", icon: Monitor },
  { slug: "online-orders", title: "Online Orders", desc: "Explore online orders", icon: Globe },
];

function Group({ title, items }: { title: string; items: Item[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.slug}
                to="/software/pos/$child"
                params={{ child: it.slug }}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                  {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PosIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="Retail POS" items={retail} />
        <Group title="Restaurant POS" items={restaurant} />
      </PageShell>
      <TopRelatedDigitalItems hub="pos" />
    </>
  );
}

const data = {
  eyebrow: "Software",
  title: "POS Software",
  sub: "Retail & restaurant point of sale.",
  intro:
    "Fast, reliable, and built for the floor. Our POS handles retail checkout, restaurant orders, table management, kitchen display, payments, inventory and loyalty — all on iPad, Android tablet or Windows. Works offline, syncs everywhere, and integrates cleanly with your accounting and e-commerce stack.",
  features: [
    { icon: ShoppingBag, title: "Lightning checkout", description: "Barcode scan, quick keys, split payments and discounts." },
    { icon: Receipt, title: "Receipts & invoices", description: "Email, print and digital receipts with custom branding." },
    { icon: CreditCard, title: "Integrated payments", description: "Card, contactless, wallets and BNPL — all in one terminal." },
    { icon: Package, title: "Live inventory", description: "Stock decrements in real time across all locations." },
    { icon: Smartphone, title: "Mobile-first", description: "iPad, Android tablet and handheld scanner support." },
    { icon: BarChart3, title: "Multi-location reporting", description: "Sales, staff and inventory dashboards across every store." },
  ],
  process: [
    { step: "01", title: "Setup", description: "Catalog import, tax setup and payment gateway configuration." },
    { step: "02", title: "Hardware", description: "Tablet, printer, cash drawer and card reader selection." },
    { step: "03", title: "Train", description: "Staff training and pilot run with one location." },
    { step: "04", title: "Roll out", description: "Multi-location rollout with central reporting." },
  ],
  deliverables: [
    "Configured POS for every register and location",
    "Catalog with categories, modifiers and pricing",
    "Hardware kits (tablet, printer, drawer, scanner)",
    "Integrated payments with your provider",
    "Inventory and accounting integration",
    "Staff training and SOPs",
  ],
  techStack: ["Custom POS (React + Capacitor)", "Square", "Stripe Terminal", "Toast", "Lightspeed APIs", "Supabase", "Offline-first sync"],
  pricing: [
    { name: "Single store", price: "$2k", period: "setup", features: ["1 location", "Up to 3 registers", "Standard hardware kit", "Basic reporting"] },
    { name: "Multi-store", price: "$8k", period: "setup", highlighted: true, features: ["Up to 5 locations", "Centralized inventory", "Loyalty + gift cards", "Advanced reporting"] },
    { name: "Enterprise", price: "Custom", features: ["Unlimited locations", "Custom integrations", "Dedicated support", "SLA + 24/7 uptime"] },
  ],
  faqs: [
    { q: "Will it work offline?", a: "Yes — orders, payments and inventory work fully offline and sync automatically when connection returns." },
    { q: "Which payment processors do you support?", a: "Stripe Terminal, Square, Adyen, SumUp and most major regional processors. We help you pick based on fees and features." },
    { q: "Can we use our existing hardware?", a: "If it's iPad, Android tablet, or a modern Windows PC with a USB receipt printer and scanner, yes. We'll audit your kit during setup." },
    { q: "Restaurant features?", a: "Yes — table management, course timing, kitchen display, modifiers, split checks and tipping flows." },
  ],
};
